import { Col, DatePicker, Form, Input, Row } from 'antd';
import { FormSelect } from '../../../components/form/FormBasic';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useState } from 'react';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import {
  GRAM_OPTIONS,
  WEIGHT_UNITS,
  POUND_OPTIONS,
} from '../../../enums/ScaleVerificationWeightsEnum';
import DefaultButton from '../../../components/buttons/DefaultButton';
import { REGEX_ALL_ALLOWED_SAFE_FILTER, REGEX_ALPHA_NUMERIC_DASH_SPACE_SAFE_FILTER, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER } from '../../../constants/regex';

interface PriceRecordFormProps {
  form: FormInstance;
  handleNext: () => void;
  handleCancel: () => void;
  inspectorOptions?: any[];
  isModal: boolean;
  isCitizen?: boolean;
  finishRegistrationClicked?: number;
  triggeredFromTask: boolean;
  taskData: any;
  disabled?: boolean;
  isViewMode?: boolean;
}

const CollectionSessionInfoForm: React.FC<PriceRecordFormProps> = ({
  form,
  handleNext,
  isViewMode,
  handleCancel,
}) => {
  const { t } = useTranslation('price');

  const [weightUnit, setWeightUnit] = useState(null);

  const getWeightOptions = (unitType: any) => {
    if (unitType === 'grams') return GRAM_OPTIONS;
    if (unitType === 'pounds') return POUND_OPTIONS;
    return [];
  };

  const minVal = useWatch('minCapacity', form);

  const disableFutureDates = (current: any) => {
    return current && current > dayjs().endOf('day');
  };

  return (
    <div className="m-0 w-full p-4">
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('inspection:generalInformation')}
        </h2>
      </div>

      <Form
        form={form}
        name="collectionSessionInfoDataForm"
        layout="vertical"
        onFinish={handleNext}
        initialValues={{
          date: dayjs()
        }}
      >
        <Row gutter={[24, 8]}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('scaleVerificationReportNo')}
              name="scaleVerificationReportNo"
              validateFirst
              rules={[
                { required: true, message: t('scaleVerificationReportNoRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('scaleVerificationReportNoSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allowing letters, numbers, dash and space
                    // const regex = /  ^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9\-\s]*$/;
                    const regex = REGEX_ALPHA_NUMERIC_DASH_SPACE_SAFE_FILTER;
                    return regex.test(String(value.trim()))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('scaleVerificationReportNoMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('scaleVerificationReportNoMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('enterReportNo')} disabled={isViewMode} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('companyName')}
              name="companyName"
              validateFirst
              rules={[
                { required: true, message: t('companyNameRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('companyNameSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis
                    // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9 ,\-\/\.\(\)]*$/;
                    const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                    return regex.test(String(value))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('companyNameMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('companyNameMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('enterCompanyName')} disabled={isViewMode} />
            </Form.Item>
          </Col>
          <Col xs={24} md={24}>
            <Form.Item
              label={t('address')}
              name="address"
              validateFirst
              rules={[
                { required: true, message: t('addressRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('addressSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    // const regex = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü\s,.\-!?;:()"'&@#$%^*+=/]+$/;                 
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('addressMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 200) {
                      return Promise.reject(t('addressMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.TextArea placeholder={t('enterAddress')} disabled={isViewMode} />
            </Form.Item>
          </Col>
          <Col xs={24} md={24}>
            <Form.Item
              name="date"
              label={t('dateLabel')}
              rules={[{ required: true, message: t('dateFieldRequired') }]}
            >
              <DatePicker
                className="w-full"
                showTime={false}
                showSecond={false}
                disabledDate={disableFutureDates}
                format="YYYY-MM-DD"
                disabled={isViewMode}
                inputReadOnly={true}
              ></DatePicker>
            </Form.Item>
          </Col>

          <Col xs={24} md={24}>
            <FormSelect
              name="weightType"
              label={t('weightUnit')}
              rules={[{ required: true, message: t('weightUnitRequired') }]}
              placeholder={t('weightFieldPlaceholder')}
              disabled={isViewMode}
              allowClear={false}
              options={WEIGHT_UNITS.map((unit) => ({ value: unit.value, label: t(unit.label) }))}
              onChange={(value) => {
                setWeightUnit(value);
                form.setFieldsValue({ capacityUnit: value });
                form.setFieldsValue({ maxCapacity: null });
                form.setFieldsValue({ minCapacity: null });
              }}
            ></FormSelect>
          </Col>
          <Col xs={24} md={12}>
            <FormSelect
              name="minCapacity"
              label={t('minWeight')}
              rules={[{ required: true, message: t('minWeightRequired') }]}
              placeholder={t('weightFieldPlaceholder')}
              disabled={!weightUnit || isViewMode}
              allowClear={false}
              options={getWeightOptions(weightUnit).map((w) => ({ value: w, label: w }))}
              onChange={(value) => {
                form.setFieldsValue({ maxCapacity: null });
                form.setFieldsValue({ minCapacity: value });
              }}
            ></FormSelect>
          </Col>
          <Col xs={24} md={12}>
            <FormSelect
              name="maxCapacity"
              label={t('maxWeight')}
              rules={[{ required: true, message: t('maxWeightRequired') }]}
              placeholder={t('weightFieldPlaceholder')}
              disabled={!weightUnit || isViewMode}
              allowClear={false}
              options={(() => {
                const weights = getWeightOptions(weightUnit);
                const minIndex = minVal ? weights.indexOf(minVal) : -1;

                return weights.map((w) => ({
                  value: w,
                  label: w,
                  disabled: minIndex !== -1 && weights.indexOf(w) < minIndex,
                }));
              })()}
              onChange={(value) => {
                form.setFieldsValue({ maxCapacity: value });
              }}
            ></FormSelect>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('serialNumber')}
              name="serialNumber"
              validateFirst
              rules={[
                { required: true, message: t('serialNumberRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('serialNumberSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters              
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('serialNumberMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('serialNumberMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('enterSerialNumber')} disabled={isViewMode} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('model')}
              name="model"
              validateFirst
              rules={[
                { required: true, message: t('modelRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('modelSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters              
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('modelMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('modelMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('enterModel')} disabled={isViewMode} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('brand')}
              name="brand"
              validateFirst
              rules={[
                { required: true, message: t('brandScaleRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('brandScaleSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters              
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('brandScaleMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('brandScaleMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('enterBrand')} disabled={isViewMode} />
            </Form.Item>{' '}
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={t('area')}
              name="area"
              validateFirst
              rules={[
                { required: true, message: t('areaRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('areaSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters              
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 3) {
                      return Promise.reject(t('areaMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('areaMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('enterArea')} disabled={isViewMode} />
            </Form.Item>{' '}
          </Col>
        </Row>
      </Form>

      {!isViewMode && (
        <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row md:pl-3">
          <DefaultButton handleOnClick={handleCancel} width="100%">
            {t('cancel')}
          </DefaultButton>
          <PrimaryButton
            handleOnClick={handleNext}
            disabled={isViewMode}
            width="100%"
            hideBgGradient={true}
          >
            {t('goNext')}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default CollectionSessionInfoForm;
